import React, { useRef, useEffect, useState } from 'react';
import { isNumber } from 'lodash';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { ESGImpactHexBorderMap } from 'shared/constants/ESGImpactHexCodesMap';

import { CustomTooltip } from 'ui';
import { useReportPage } from 'shared/hooks';

import GougeChartSvg from './GougeChartSvg';

const prepareValue = number => number * 10;

const useStyles = makeStyles({
  container: {
    position: 'relative',
    textAlign: 'center',
    marginTop: useReportPage ? 20 : 0,
    marginBottom: useReportPage ? -35 : 0,
  },
  scoreBox: {
    position: 'absolute',
    left: '50%',
    bottom: useReportPage ? '22%' : '10%',
    transform: `translateX(-50%) ${useReportPage ? 'scale(0.6)' : ''}`,
  },
});

const GougeChart = ({
  width,
  height,
  value,
  worst,
  average,
  best,
  score,
  color,
  title,
  labels,
  factor,
}) => {
  const ref = useRef(null);
  const [chart, setChart] = useState();
  const classes = useStyles();
  const { t } = useTranslation();

  useEffect(() => {
    // eslint-disable-next-line no-new
    setChart(
      new GougeChartSvg({
        current: ref.current,
        small: useReportPage,
        width,
        height,
        value,
        worst,
        average,
        best,
        color,
        labels,
        factor,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chart) chart.init();
  }, [chart]);

  useEffect(() => {
    if (chart && isNumber(value)) {
      chart.destroy();
      chart.value = prepareValue(value);
      chart.init();
    }
  }, [chart, value]);

  useEffect(() => {
    if (chart && isNumber(worst)) {
      chart.destroy();
      chart.worst = prepareValue(worst);
      chart.init();
    }
  }, [chart, worst]);

  useEffect(() => {
    if (chart && isNumber(average)) {
      chart.destroy();
      chart.average = prepareValue(average);
      chart.init();
    }
  }, [chart, average]);

  useEffect(() => {
    if (chart && color) {
      chart.destroy();
      chart.color = color;
      chart.init();
    }
  }, [chart, color]);

  useEffect(() => {
    if (chart && isNumber(best)) {
      chart.destroy();
      chart.best = prepareValue(best);
      chart.init();
    }
  }, [chart, best]);

  return (
    <CustomTooltip
      placement="right"
      title={
        <>
          <Typography variant="h6">{t('Peer group comparison')}</Typography>
          <Typography variant="caption">
            {labels ? labels.best : t('Best')}: <strong>{best}</strong>
          </Typography>
          <br />
          <Typography variant="caption">
            {t('Average')}: <strong>{average}</strong>
          </Typography>
          <br />
          <Typography variant="caption">
            {labels ? labels.worst : t('Worst')}: <strong>{worst}</strong>
          </Typography>
        </>
      }
      arrow
    >
      <div ref={ref} className={classes.container}>
        {title && (
          <Box mb={useReportPage ? 1 : 3}>
            <Typography align="center" variant="h5">
              <strong>{title}</strong>
            </Typography>
          </Box>
        )}
        <div className={classes.scoreBox}>{score}</div>
      </div>
    </CustomTooltip>
  );
};

GougeChart.defaultProps = {
  width: useReportPage ? 190 : 425,
  height: useReportPage ? 180 : 245,
  color: ESGImpactHexBorderMap.medium,
  title: undefined,
  labels: undefined,
  factor: undefined,
};

GougeChart.propTypes = {
  value: PropTypes.number.isRequired,
  worst: PropTypes.number.isRequired,
  average: PropTypes.number.isRequired,
  best: PropTypes.number.isRequired,
  score: PropTypes.node.isRequired,
  title: PropTypes.node,
  color: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  labels: PropTypes.shape({
    average: PropTypes.string,
    worst: PropTypes.string,
    best: PropTypes.string,
  }),
  factor: PropTypes.number,
};

export default GougeChart;
